import { FC, useState } from 'react';

import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import {
  DELIVERY_CHANNEL_CONFIG,
  DeliveryChannel,
  GiftCouponByCode,
  GiftCouponRedeemStatus,
  NotificationType,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import { ContactEmailLink } from '../../components/ContactEmailLink';
import CouponNoticeModal from './CouponNoticeModal';
import { GiftBoxNotice, ValidPeriodNotice } from './CouponNotices';
import CouponProductCard from './CouponProductCard';
import ExchangeCautionNotice from './ExchangeCautionNotice';
import {
  formatKoreanDate,
  getNextCouponSendSlot,
  RedeemStatusMessage,
} from './giftCouponRedeem.utils';
import SendScheduleCard from './SendScheduleCard';

interface CouponStatusViewProps {
  coupon: GiftCouponByCode;
  statusMessage: RedeemStatusMessage;
  imageUrl: string;
  itemName: string;
  couponCode: string;
  brandName: string | null;
  validPeriod?: string;
}

const CouponStatusView: FC<CouponStatusViewProps> = ({
  coupon,
  statusMessage,
  imageUrl,
  itemName,
  couponCode,
  brandName,
  validPeriod,
}) => {
  const selectedUnit =
    coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object'
      ? coupon.selectedChoiceUnit
      : null;

  // 교환신청 완료(Requested) · 발송완료(Delivered)로 진입할 때마다 안내 모달을 한 번 띄운다.
  const showNoticeModal =
    coupon.redeemStatus === GiftCouponRedeemStatus.Requested ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Delivered;
  const [isNoticeModalOpen, setIsNoticeModalOpen] = useState(showNoticeModal);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white px-4 py-8">
      <div className="mx-auto max-w-md">
        {/* 상태 메시지 */}
        <div
          className={`rounded-xl p-6 text-center ${
            statusMessage.type === 'success'
              ? 'border border-stone-200 bg-stone-50'
              : statusMessage.type === 'info'
              ? 'border border-stone-200 bg-stone-50'
              : 'border border-red-100 bg-red-50'
          }`}
        >
          {statusMessage.type === 'success' ? (
            <CheckCircleSolidIcon className="mx-auto h-10 w-10 text-stone-600" />
          ) : statusMessage.type === 'info' ? (
            <CheckCircleIcon className="mx-auto h-10 w-10 text-stone-500" />
          ) : (
            <ExclamationCircleIcon className="mx-auto h-10 w-10 text-red-400" />
          )}
          <h2 className="mt-3 text-lg font-medium text-stone-800">
            {statusMessage.title}
          </h2>
          {statusMessage.message && (
            <p className="mt-1 whitespace-pre-line text-sm text-stone-500">
              {statusMessage.message}
            </p>
          )}
        </div>

        {/* Delivered: 알림톡/문자 안내 — 실제 수신 채널이 갈릴 수 있어 항상 둘 다 안내하고, 발송 매체 타입에 따라 순서만 바꾼다 */}
        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered &&
          (() => {
            const channel = coupon.gifticon?.deliveryChannel;
            const config =
              channel && channel in DeliveryChannel
                ? DELIVERY_CHANNEL_CONFIG[channel as DeliveryChannel]
                : null;
            const isAlimTalkFirst =
              !config || config.notificationType === NotificationType.AlimTalk;
            return (
              <div className="mt-4 overflow-hidden rounded-xl border border-stone-200 bg-white text-center shadow-sm">
                <div className="px-5 py-4">
                  <p className="text-base font-bold text-stone-800">
                    {isAlimTalkFirst
                      ? '알림톡(카카오톡) 또는 문자를 확인해 주세요'
                      : '문자 또는 알림톡(카카오톡)을 확인해 주세요'}
                  </p>
                  <p className="mt-1.5 text-xs text-stone-600">
                    {isAlimTalkFirst
                      ? '카카오톡 알림톡으로 발송되며, 수신 환경에 따라 문자로 도착할 수 있습니다.'
                      : '문자로 발송되며, 수신 환경에 따라 카카오톡 알림톡으로 도착할 수 있습니다.'}
                    <br />
                    <span className="break-keep pt-1 font-bold">
                      알림톡과 문자 모두에서 확인이 어려우시면
                      <br />
                      하단 문의 메일로 말씀주세요.
                    </span>
                  </p>
                  {config?.senderNumber && (
                    <div className="mt-3 inline-flex items-center gap-2.5 rounded-lg border border-stone-200 bg-stone-50 px-5 py-2.5">
                      <span className="text-xs font-medium text-stone-400">
                        발신번호
                      </span>
                      <span className="text-base font-bold text-stone-800">
                        {config.senderNumber}
                      </span>
                    </div>
                  )}
                </div>
                {coupon.gifticon?.deliveredAt && (
                  <div className="border-t border-stone-100 bg-stone-50 px-5 py-2.5">
                    <p className="text-sm font-semibold text-stone-700">
                      {formatKoreanDate(new Date(coupon.gifticon.deliveredAt))} 발송
                    </p>
                  </div>
                )}
              </div>
            );
          })()}

        {/* Requested: 발송 예정 안내 */}
        {coupon.redeemStatus === GiftCouponRedeemStatus.Requested &&
          coupon.recipient?.requestedAt &&
          (() => {
            const reqAt = new Date(coupon.recipient.requestedAt);
            const sendAt = getNextCouponSendSlot(reqAt);
            return <SendScheduleCard requestedAt={reqAt} expectedSendAt={sendAt} />;
          })()}

        {/* Requested/Delivered 공통: 유효기간 + 수령방법 안내 */}
        {(coupon.redeemStatus === GiftCouponRedeemStatus.Requested ||
          coupon.redeemStatus === GiftCouponRedeemStatus.Delivered) && (
          <div className="mt-4 space-y-3">
            <ValidPeriodNotice validPeriod={validPeriod} />
            <GiftBoxNotice />
          </div>
        )}

        {/* 교환신청 완료(Requested) / 발송완료(Delivered) 시 교환·사용 유의사항을 사진 위에 노출 */}
        {(coupon.redeemStatus === GiftCouponRedeemStatus.Requested ||
          coupon.redeemStatus === GiftCouponRedeemStatus.Delivered) && (
          <ExchangeCautionNotice className="mt-4" />
        )}

        {/* 상품 정보 */}
        <CouponProductCard
          imageUrl={imageUrl}
          itemName={itemName}
          couponCode={couponCode}
          brandName={brandName}
          selectedUnit={selectedUnit}
        />

        {!statusMessage.hideContact && <ContactEmailLink />}
      </div>

      <CouponNoticeModal
        open={isNoticeModalOpen}
        validPeriod={validPeriod}
        onClose={() => setIsNoticeModalOpen(false)}
      />
    </div>
  );
};

export default CouponStatusView;
